import { DealPipelineInvestmentWorkflowStateFiltersQuery, EditStrategyPageInvestmentQuery, Security_Status } from '@/__generated__/graphql';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { Accordion } from '@/components/basicComponents/accordion';
import ActionDropdown from '@/components/basicComponents/actionDropdown';
import Button from '@/components/basicComponents/button';
import { EllipsisWithTooltip } from '@/components/basicComponents/ellipsisWithTooltip';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Tag from '@/components/basicComponents/tag';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitleWrap } from '@/components/header/pageTitle';
import { Loader } from '@/components/loaders/loader';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { SORT_ORDER } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { themeVar } from '@/styles/theme';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MainWrap, PaddingWrap } from '../../../styles/common';
import UpdateSecurityStatus from '../modals/updateSecurityStatus';
import { DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY, INVESTMENT_VEHICLES_QUERY } from '../queries';
import { AllocationSettingsModalPage } from './editModalPages/allocationSettings';
import { BasicInfoModalPage } from './editModalPages/basicInfo';
import { ClosingsModalPage } from './editModalPages/closings';
import { ManagerModalPage } from './editModalPages/manager';
import { OwnersModalPage } from './editModalPages/owners';
import { VehiclesModalPage } from './editModalPages/vehicles';
import AssignManager from './modals/assignManager';
import DeleteStrategy from './modals/deleteStrategy';
import { EDIT_STRATEGY_PAGE_INVESTMENT_QUERY, EDIT_STRATEGY_PAGE_INVESTMENTS_QUERY } from './queries';
import { IModifyStrategySectionData, InvestmentVehicleDto } from './types';
import { getSectionsData } from './utils';

const defaultSectionsData: IModifyStrategySectionData[] = [
  { title: 'Basic Info', name: 'basicInfo', formData: {}, data: [] },
  { title: 'Allocation Settings', name: 'allocationSettings', formData: {}, data: [] },
  { title: 'Closings', name: 'closings', formData: {}, data: [] },
  { title: 'Vehicles', name: 'vehicles', formData: {}, data: [] },
  { title: 'Owners', name: 'owners', formData: {}, data: [] }
];

const ModifyStrategy = () => {
  const goBack = useGoBack();
  const { isMobile, isTablet } = useResponsive();
  const { searchParams, setSearchParams } = useSearchParamsManager();

  const backToTitle = searchParams.get('backToTitle') || '';
  const investmentId = searchParams.get('investment') || '';
  const modalPageType: string | null = searchParams.get('modalPageType') || null;

  const [workflowStates, setWorkflowStates] = useState<DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters']>([]);
  const [investmentData, setInvestmentData] = useState<EditStrategyPageInvestmentQuery['Investment'] | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [activeSection, setActiveSection] = useState('basicInfo');
  const [anchorHeight, setAnchorHeight] = useState(70);
  const [strategiesSelectOptions, setStrategiesSelectOptions] = useState<ISelectValue[]>([]);
  const [sectionsData, setSectionsData] = useState<IModifyStrategySectionData[]>([]);
  const [currentFormData, setCurrentFormData] = useState<any>({});
  const [investmentVehicles, setInvestmentVehicles] = useState<InvestmentVehicleDto[]>([]);
  const [scrollPosition, setScrollPosition] = useState<number>(0);

  const anchorNavRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (anchorNavRef.current) {
      setAnchorHeight(isMobile || isTablet ? anchorNavRef.current.offsetHeight + 50 : anchorNavRef.current.offsetHeight + 5);
    }
  }, []);

  useQuery<{
    InvestmentWorkflowStateFilters: DealPipelineInvestmentWorkflowStateFiltersQuery['InvestmentWorkflowStateFilters'];
  }>(DEAL_PIPELINE_INVESTMENT_WORKFLOW_STATE_FILTERS_QUERY, {
    onCompleted: ({ InvestmentWorkflowStateFilters }) => {
      setWorkflowStates(InvestmentWorkflowStateFilters || []);
    }
  });

  const { networkStatus: investmentNetworkStatus, refetch: investmentRefetch } = useQuery(EDIT_STRATEGY_PAGE_INVESTMENT_QUERY, {
    skip: !investmentId,
    variables: { id: investmentId },
    onCompleted: ({ Investment }) => {
      isInitialLoadRef.current = false;
      setInvestmentData(Investment);
      setSectionsData(getSectionsData(Investment));
    },
    onError: (error) => {
      console.error(error);
      setSectionsData(defaultSectionsData);
      isInitialLoadRef.current = false;
    }
  });

  const { loading: investmentsLoading } = useQuery(EDIT_STRATEGY_PAGE_INVESTMENTS_QUERY, {
    skip: !investmentData?.managerId,
    variables: {
      page: 1,
      rowsPerPage: 1000,
      sort: 'DEAL',
      order: SORT_ORDER.ASC,
      managerIdFilter: investmentData?.managerId
    },
    onCompleted: (data) => {
      const selectOptions = data?.Investments?.data?.map((strategy) => createSimpleSelectOption(strategy.id, strategy.name));
      setStrategiesSelectOptions(selectOptions || []);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { refetch: investmentVehiclesRefetch } = useQuery<{
    investmentVehicles: InvestmentVehicleDto[];
  }>(INVESTMENT_VEHICLES_QUERY, {
    skip: !investmentId,
    variables: {
      data: {
        investmentId,
        seriesCodes: []
      }
    },
    onCompleted: (data) => {
      setInvestmentVehicles(data.investmentVehicles);
    }
  });

  useEffect(() => {
    if (!modalPageType || !sectionsData.length || Object.keys(currentFormData || {}).length) return;

    const section = sectionsData?.find((section) => section.name === modalPageType);
    setCurrentFormData(section?.formData || {});
  }, [modalPageType, sectionsData]);

  const handleEditModalPage = ({ type = '', formData = {} }: { type?: string; formData?: any }) => {
    setCurrentFormData(formData);
    setScrollPosition(type ? window.scrollY : 0);
    setSearchParams(
      (prev) => {
        if (type) {
          prev.set('modalPageType', type);
        } else {
          prev.delete('modalPageType');
        }
        return prev;
      },
      { replace: true }
    );

    if (!type) {
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    setActiveSection(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - anchorHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleSelectStrategy = (strategyId: string) => {
    setSearchParams(
      (prev) => {
        prev.set('investment', strategyId);
        return prev;
      },
      { replace: true }
    );
  };

  if (isInitialLoadRef.current) {
    return <LoaderOnWholeScreen />;
  }

  if (modalPageType) {
    switch (modalPageType) {
      case 'manager':
        return (
          <ManagerModalPage
            title="Manager"
            investmentId={investmentData?.id ?? ''}
            formData={{ managerId: investmentData?.managerId ?? '', newManagerId: investmentData?.managerId ?? '' }}
            handleCloseEditModalPage={() => handleEditModalPage({})}
          />
        );
      case 'basicInfo':
        return (
          <BasicInfoModalPage
            title="Basic Info"
            investmentId={investmentData?.id ?? ''}
            formData={currentFormData}
            investment={{
              securityStatus: investmentData?.securityStatus as Security_Status,
              tags: investmentData?.tags,
              subAssetClasses: investmentData?.subAssetClasses,
              pacingStartingYear: investmentData?.pacingStartingYear,
              pacingStartingQuarter: investmentData?.pacingStartingQuarter
            }}
            investmentRefetch={investmentRefetch}
            handleCloseEditModalPage={() => handleEditModalPage({})}
          />
        );
      case 'allocationSettings':
        return (
          <AllocationSettingsModalPage
            title="Allocation Settings"
            investmentId={investmentData?.id ?? ''}
            formData={currentFormData}
            investment={{
              securityStatus: investmentData?.securityStatus as Security_Status,
              advisoryFirms: investmentData?.advisoryFirms,
              showToAllAdvisoryFirms: investmentData?.showToAllAdvisoryFirms
            }}
            investmentRefetch={investmentRefetch}
            handleCloseEditModalPage={() => handleEditModalPage({})}
          />
        );
      case 'closings':
        return (
          <ClosingsModalPage
            title="Closings"
            investmentId={investmentData?.id ?? ''}
            formData={currentFormData}
            investmentRefetch={investmentRefetch}
            handleCloseEditModalPage={() => handleEditModalPage({})}
          />
        );
      case 'vehicles':
        return (
          <VehiclesModalPage
            title="Vehicles"
            investmentId={investmentData?.id ?? ''}
            investmentVehicles={investmentVehicles}
            investmentVehiclesRefetch={investmentVehiclesRefetch}
            handleCloseEditModalPage={() => handleEditModalPage({})}
          />
        );
      case 'owners':
        return (
          <OwnersModalPage
            title="Owners"
            investmentId={investmentData?.id ?? ''}
            formData={currentFormData}
            investmentRefetch={investmentRefetch}
            handleCloseEditModalPage={() => handleEditModalPage({})}
          />
        );
      default:
        return <></>;
    }
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'assign-manager' && (
        <AssignManager
          isOpen={modalWindow.isOpen}
          strategy={{
            id: investmentData?.id || '',
            name: investmentData?.name || '',
            manager: investmentData?.managerId || ''
          }}
          strategiesSelectOptions={strategiesSelectOptions}
          onClose={() => setModalWindow({ type: '', isOpen: false })}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'delete-strategy' && (
        <DeleteStrategy
          isOpen={modalWindow.isOpen}
          strategy={{ id: investmentData?.id || '', name: investmentData?.name || '' }}
          onClose={() => setModalWindow({ type: '', isOpen: false })}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'update-security-status' && (
        <UpdateSecurityStatus
          isOpen={modalWindow.isOpen}
          investmentId={investmentData?.id ?? ''}
          workflowStates={workflowStates}
          source="modify"
          onClose={() => setModalWindow({ type: '', isOpen: false })}
        />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/dealPipeline' })} backToTitle={backToTitle} />} />
        <PageTitleWrap withBorder>
          Edit Strategy
          <ActionDropdownWrapper>
            {investmentData?.workflowInstance?.currentState?.label && (
              <Tag title={investmentData?.workflowInstance?.currentState?.label} size="lg" styleType="neutral" />
            )}
            <ActionDropdown
              options={[{ name: 'Update Status', onClick: () => setModalWindow({ type: 'update-security-status', isOpen: true }) }]}
              size="sm"
              styleType="outline"
            />
          </ActionDropdownWrapper>
        </PageTitleWrap>
      </MainWrap>
      <MainWrap>
        <PaddingWrap>
          <SectionsNavWrapper isMobile={isMobile} isTablet={isTablet} ref={anchorNavRef}>
            <SectionAnchorNavWrapper isMobile={isMobile} isTablet={isTablet}>
              {sectionsData.map((item) => (
                <SectionAnchorNavItem key={item.name} isActive={activeSection === item.name} onClick={() => scrollToSection(item.name)}>
                  {item.title}
                </SectionAnchorNavItem>
              ))}
            </SectionAnchorNavWrapper>
          </SectionsNavWrapper>
          <SectionsWrapper>
            {investmentData?.managerId && (
              <Accordion title="Manager" onEdit={() => handleEditModalPage({ type: 'manager' })}>
                <SectionDataItemWrapper isMobile={isMobile}>
                  <SectionDataItem>Manager</SectionDataItem>
                  <SectionDataItem isBold>{investmentData?.managerId}</SectionDataItem>
                </SectionDataItemWrapper>
              </Accordion>
            )}
            <SelectStrategySection isMobile={isMobile}>
              <StrategySelectWrapper>
                {strategiesSelectOptions.length > 1 ? (
                  <StrategySelect
                    value={investmentId}
                    setValue={handleSelectStrategy}
                    options={[{ label: 'Select A Strategy', value: '' }, ...strategiesSelectOptions]}
                    loading={investmentsLoading}
                    width="100%"
                  />
                ) : (
                  <EllipsisWithTooltip title={investmentData?.name} maxContentWidth={300}>
                    <StrategyLabel>{investmentData?.name}</StrategyLabel>
                  </EllipsisWithTooltip>
                )}
              </StrategySelectWrapper>
              <SelectStrategySectionButtons isMobile={isMobile}>
                <Button styleType="outline" size="lg" onClick={() => setModalWindow({ type: 'assign-manager', isOpen: true })}>
                  {investmentData?.managerId ? 'Change Manager' : 'Assign Manager'}
                </Button>
                {investmentData?.workflowInstance?.currentState?.name === 'draft' && (
                  <Button styleType="outline" size="lg" onClick={() => setModalWindow({ type: 'delete-strategy', isOpen: true })}>
                    Delete
                  </Button>
                )}
              </SelectStrategySectionButtons>
            </SelectStrategySection>
            {investmentNetworkStatus === NetworkStatus.setVariables ? (
              <Loader />
            ) : (
              <>
                {sectionsData.map((section) => (
                  <Accordion
                    key={section.name}
                    id={section.name}
                    title={section.title}
                    onEdit={() => handleEditModalPage({ type: section.name, formData: section.formData })}
                  >
                    {section.name === 'vehicles' ? (
                      <>
                        {investmentVehicles?.length ? (
                          <div>
                            {investmentVehicles?.map((vehicle) => (
                              <SectionData key={vehicle.id} isMobile={isMobile}>
                                <SectionDataItemWrapper isMobile={isMobile}>
                                  <SectionDataItem>Vehicle Code</SectionDataItem>
                                  <SectionDataItem isBold>{vehicle.id}</SectionDataItem>
                                </SectionDataItemWrapper>
                                {vehicle.name && (
                                  <SectionDataItemWrapper isMobile={isMobile}>
                                    <SectionDataItem>Vehicle Name</SectionDataItem>
                                    <SectionDataItem isBold>{vehicle.name}</SectionDataItem>
                                  </SectionDataItemWrapper>
                                )}
                                {typeof vehicle.taxExempt === 'boolean' && (
                                  <SectionDataItemWrapper isMobile={isMobile}>
                                    <SectionDataItem>Tax Status</SectionDataItem>
                                    <SectionDataItem isBold>{vehicle.taxExempt ? 'Exempt' : 'Taxable'}</SectionDataItem>
                                  </SectionDataItemWrapper>
                                )}
                              </SectionData>
                            ))}
                          </div>
                        ) : (
                          <SectionZeroState>
                            <SectionZeroStateText>No information has been added yet.</SectionZeroStateText>
                            <Button
                              onClick={() => handleEditModalPage({ type: section.name })}
                              styleType="outline"
                              icon={<AddButtonIcon fill={themeVar('font.action')} />}
                              size="md"
                              width="max-content"
                            >
                              Add Information
                            </Button>
                          </SectionZeroState>
                        )}
                      </>
                    ) : (
                      <>
                        {!section.data.length || !section.data.some((item: any) => item.value) ? (
                          <SectionZeroState>
                            <SectionZeroStateText>No information has been added yet.</SectionZeroStateText>
                            <Button
                              onClick={() => handleEditModalPage({ type: section.name })}
                              styleType="outline"
                              icon={<AddButtonIcon fill={themeVar('font.action')} />}
                              size="md"
                              width="max-content"
                            >
                              Add Information
                            </Button>
                          </SectionZeroState>
                        ) : (
                          <SectionData isMobile={isMobile}>
                            {section.data.map(
                              (item: any) =>
                                item.value && (
                                  <SectionDataItemWrapper key={item.title} isMobile={isMobile}>
                                    <SectionDataItem>{item.title}</SectionDataItem>
                                    <SectionDataItem isBold>{item.value}</SectionDataItem>
                                  </SectionDataItemWrapper>
                                )
                            )}
                          </SectionData>
                        )}
                      </>
                    )}
                  </Accordion>
                ))}
              </>
            )}
          </SectionsWrapper>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

export default ModifyStrategy;

const ActionDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
`;

export const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border.base};
  background: ${({ theme }) => theme.layer[1]};
`;

const SectionsNavWrapper = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  display: flex;
  gap: 20px;
  padding: 10px 0;
  position: sticky;
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : '0')};
  z-index: 99;
  background-color: ${themeVar('layer.base')};
`;

const SectionAnchorNavWrapper = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 10px 20px 10px;
  position: sticky;
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : '0')};
  z-index: 99;
  background-color: ${themeVar('layer.base')};
`;

const SectionAnchorNavItem = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: ${({ isActive }) => (isActive ? themeVar('font.base') : themeVar('font.action'))};
  cursor: pointer;
  border-bottom: ${({ isActive }) => (isActive ? `4px solid ${themeVar('menu.active')}` : '4px solid transparent')};
  width: max-content;

  svg {
    path {
      fill: ${({ isActive }) => (isActive ? themeVar('font.base') : themeVar('font.action'))};
    }
  }
`;

const SelectStrategySection = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '10px' : '20px')};
  padding: 4px 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const SelectStrategySectionButtons = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 10px;
  white-space: nowrap;
`;

const StrategySelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const StrategyLabel = styled.div<{ isGroupedEntity?: boolean }>`
  font-size: 28px;
  font-weight: 700;
  line-height: 40px;
  padding: 10px 0;
  white-space: ${({ isGroupedEntity }) => (isGroupedEntity ? 'nowrap' : 'normal')};
  color: ${({ isGroupedEntity }) => (isGroupedEntity ? themeVar('font.action') : themeVar('font.strong'))};
`;

const StrategySelect = styled(Select)`
  color: ${themeVar('font.action')};
  font-size: 28px;
  font-weight: 700;
  line-height: 40px;
  background-color: transparent;
  border: none;
  gap: 10px;
  padding-left: 0;
  padding-right: 0;
  cursor: pointer;

  #dropdownSelect {
    font-size: 19px;
    line-height: 26.6px;
    font-weight: 400;
    min-width: 300px;
    left: -20px;
  }
`;

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 50px;
`;

const SectionZeroState = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const SectionZeroStateText = styled.div`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${({ theme }) => theme.font.weak};
`;

const SectionData = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  padding: ${({ isMobile }) => (isMobile ? '0' : '20px')};
`;

const SectionDataItemWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  padding: 2px 10px;
`;

export const SectionDataItem = styled.div<{ isBold?: boolean }>`
  font-family: Blinker;
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 600 : 400)};
  line-height: 22.4px;
  color: ${({ theme }) => theme.font.base};
  padding: 4px;
`;
